import React from 'react';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { FileText } from 'lucide-react';
import { toast } from 'sonner';
import RichTextEditor from '../../components/RichTextEditor';
import { BM_DEFAULT_CONTRACT_TERMS_HTML } from '../../lib/defaultContractTerms';
import { SettingsSection } from './SettingsLayout';
import { useSettings } from './useSettings';
import { useAuth } from '../../contexts/AuthContext';

export default function TermsSettings() {
    const { settings, update } = useSettings();
    const { can } = useAuth();
    const readOnly = !can('settings', 'edit');

    if (!settings) return <div className="text-[color:var(--bm-text-muted)]">Loading…</div>;

    return (
        <fieldset disabled={readOnly} className={readOnly ? 'opacity-70 pointer-events-none' : ''}>
            <SettingsSection
                title="Default contract terms"
                description="These T&Cs become part of every accepted contract. Clients can view & download them from the proposal page before signing. Formatting (bold, headings, lists) is preserved on the public T&Cs page and the signed-agreement PDF."
                wide
                testId="settings-default-contract-terms-block"
            >
                <div className="sm:col-span-2 space-y-3">
                    <div className="flex justify-end">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                if ((settings.default_contract_terms || '').trim() &&
                                    !window.confirm('Replace current terms with the Beyond Measure standard T&Cs? Your current text will be overwritten (you must still click Save to persist).')) {
                                    return;
                                }
                                update('default_contract_terms', BM_DEFAULT_CONTRACT_TERMS_HTML);
                                toast.success('Loaded BM standard T&Cs — click "Save all" to persist.');
                            }}
                            className="h-8 text-[11.5px]"
                            data-testid="settings-load-default-terms-btn"
                        >
                            <FileText size={13} className="mr-1.5" />
                            Load BM standard T&amp;Cs
                        </Button>
                    </div>
                    <RichTextEditor
                        value={settings.default_contract_terms || ''}
                        onChange={(v) => update('default_contract_terms', v)}
                        minHeight={360}
                        placeholder="Type your terms & conditions here, or click 'Load BM standard T&Cs' to start from the comprehensive template."
                        testId="settings-default-contract-terms-editor"
                    />
                </div>
            </SettingsSection>

            <SettingsSection title="Default proposal terms" description="Short summary shown on the proposal cover (above the line-items)." wide>
                <Textarea
                    value={settings.default_proposal_terms || ''}
                    onChange={e => update('default_proposal_terms', e.target.value)}
                    rows={4}
                    className="sm:col-span-2"
                />
            </SettingsSection>
        </fieldset>
    );
}
